import React, { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "../home/shared";
import { Mix } from "../home/Reskin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PageHero = ({ eyebrow, title, copy, image, video, children, testid = "page-hero" }) => (
  <section className="relative min-h-[78vh] md:min-h-[88vh] overflow-hidden flex items-center justify-center text-center" data-testid={testid}>
    {video ? (
      <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata" poster={image}>
        {video.map((v) => <source key={v} src={v} type={v.endsWith(".webm") ? "video/webm" : "video/mp4"} />)}
      </video>
    ) : (
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
    )}
    <div className="absolute inset-0 bg-[#231f20]/55" />
    <div className="relative w-full max-w-[60rem] mx-auto px-6 pb-16 pt-36">
      {eyebrow && <Reveal><p className="feral-nav uppercase tracking-[0.08em] text-xs text-[#f2e5de] mb-6">{eyebrow}</p></Reveal>}
      <Reveal delay={0.1}><h1 className="feral-display text-[#ebc754] text-[2.8rem] sm:text-6xl lg:text-[5.2rem] leading-[1.02]">{typeof title === "string" ? <Mix text={title} /> : title}</h1></Reveal>
      {copy && <Reveal delay={0.2}><p className="mt-8 mx-auto text-[#f2e5de] text-base md:text-[1.35rem] leading-[1.6] max-w-[44rem]">{copy}</p></Reveal>}
      {children && <Reveal delay={0.3}><div className="mt-12 flex flex-wrap justify-center gap-4">{children}</div></Reveal>}
    </div>
  </section>
);

export const Btn = ({ as: As = "a", variant = "solid", className = "", ...rest }) => (
  <As
    className={`inline-flex items-center justify-center gap-2 feral-nav uppercase tracking-[0.06em] text-[13px] md:text-sm h-[52px] md:h-[58px] px-8 transition-colors duration-300 ${
      variant === "solid" ? "bg-[#ebc754] text-[#9b3d0a] hover:bg-[#f2e5de]" : variant === "dark" ? "bg-[#9b3d0a] text-[#ebc754] hover:bg-[#82254d]" : "border border-[#f2e5de] text-[#f2e5de] hover:bg-[#f2e5de] hover:text-[#231f20]"
    } ${className}`}
    {...rest}
  />
);

export const Placeholder = ({ children, dark = false, testid }) => (
  <div className={`border border-dashed p-6 text-sm font-light ${dark ? "border-[#f2e5de]/40 text-[#f2e5de]/70" : "border-[#2c6052]/30 text-[#231f20]/60"}`} data-testid={testid}>
    {children}
  </div>
);

export const QuotePlaceholders = ({ labels, heading = "In their words.", testid = "section-reviews" }) => (
  <section className="bg-[#9b3d0a] feral-texture text-[#f2e5de] py-24 md:py-32 px-6 md:px-10" data-testid={testid}>
    <div className="max-w-[1500px] mx-auto">
      <Reveal><h2 className="feral-serif text-[#f2e5de] text-5xl sm:text-6xl leading-[0.95] tracking-tight mb-14">{heading}</h2></Reveal>
      <div className="grid md:grid-cols-3 gap-10">
        {labels.map((l, i) => (
          <Reveal key={`${l}-${i}`}>
            <figure className="border-t border-[#f2e5de]/30 pt-8">
              <span className="feral-serif text-6xl leading-none text-[#ebc754]">“</span>
              <Placeholder dark>Placeholder — {l.toLowerCase()} quote to be supplied.</Placeholder>
              <figcaption className="mt-5 feral-header text-[11px] font-bold tracking-[0.22em] text-[#f2e5de]/80">{l}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const field = "w-full bg-transparent border-b border-[#2c6052]/25 focus:border-[#9b3d0a] outline-none py-3 text-[#231f20] placeholder:text-[#231f20]/45 transition-colors";

export const EnquiryForm = ({ kind, fields = [], submitLabel, successText }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", group_size: "", dates: "", message: "", website: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) { setState("invalid"); return; }
    setState("sending");
    try { await axios.post(`${API}/enquiries`, { ...f, kind }); setState("done"); } catch { setState("error"); }
  };
  if (state === "done") return <p className="flex items-center gap-3 text-[#9b3d0a] text-lg" data-testid={`${kind}-success`}><Check size={20} /> {successText}</p>;
  return (
    <form noValidate onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-6" data-testid={`${kind}-form`}>
      <input aria-label="Name" placeholder="Your name *" value={f.name} onChange={set("name")} className={field} data-testid={`${kind}-name`} />
      <input aria-label="Email" type="email" placeholder="Email *" value={f.email} onChange={set("email")} className={field} data-testid={`${kind}-email`} />
      <input aria-label="Phone" placeholder="Phone" value={f.phone} onChange={set("phone")} className={field} data-testid={`${kind}-phone`} />
      {fields.includes("company") && <input aria-label="Company" placeholder="Company / organisation" value={f.company} onChange={set("company")} className={field} data-testid={`${kind}-company`} />}
      {fields.includes("group_size") && <input aria-label="Group size" placeholder="Group size" value={f.group_size} onChange={set("group_size")} className={field} data-testid={`${kind}-group-size`} />}
      {fields.includes("dates") && <input aria-label="Preferred dates" placeholder="Preferred dates" value={f.dates} onChange={set("dates")} className={field} data-testid={`${kind}-dates`} />}
      <textarea aria-label="Message" placeholder="Tell us a little about what you have in mind" rows={3} value={f.message} onChange={set("message")} className={`${field} sm:col-span-2 resize-none`} data-testid={`${kind}-message`} />
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
        <Btn as="button" variant="dark" disabled={state === "sending"} data-testid={`${kind}-submit`}>{state === "sending" ? "Sending…" : submitLabel}</Btn>
        {state === "invalid" && <span className="text-sm text-[#9b3d0a]" data-testid={`${kind}-error`}>Please add your name and a valid email.</span>}
        {state === "error" && <span className="text-sm text-[#9b3d0a]" data-testid={`${kind}-error`}>Something went wrong — please try again.</span>}
      </div>
    </form>
  );
};
